import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { briefingStepElementId } from './briefingStepElementId'
import type { BriefingStep } from './types'

function BriefingStepIndicator({
  index,
  step,
}: {
  index: number
  step: BriefingStep
}) {
  const showCheck = step.status === 'done'

  if (showCheck) {
    return (
      <span
        className='flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
        aria-hidden
      >
        <Check className='size-3' />
      </span>
    )
  }

  return (
    <span className='flex size-5 shrink-0 items-center justify-center text-xs text-muted-foreground tabular-nums'>
      {index + 1}
    </span>
  )
}

export function BriefingStepItem({
  idPrefix,
  index,
  step,
  actions,
  children,
}: {
  idPrefix: string
  index: number
  step: BriefingStep
  actions?: ReactNode
  children?: ReactNode
}) {
  const hasBody = children != null
  const hasActions = actions != null
  return (
    <li
      id={briefingStepElementId(idPrefix, step.key)}
      className='scroll-mt-4 px-3 py-3'
    >
      <div className='flex items-start gap-3'>
        <BriefingStepIndicator index={index} step={step} />
        <div className='min-w-0 flex-1 space-y-2'>
          <div className='flex items-start justify-between gap-3'>
            <div className='min-w-0 space-y-0.5'>
              <div className='flex flex-wrap items-center gap-1.5'>
                <h3 className='text-sm font-medium'>{step.title}</h3>
                <Badge variant='outline'>{step.requirednessLabel}</Badge>
              </div>
              <p className='text-xs text-muted-foreground'>
                {step.description}
              </p>
            </div>
            {hasActions && (
              <div className='flex shrink-0 flex-wrap items-center justify-end gap-2'>
                {actions}
              </div>
            )}
          </div>
          {hasBody && <div className='space-y-2'>{children}</div>}
        </div>
      </div>
    </li>
  )
}
